/**
 * Type Definitions for Legal Contract Review Client
 */

/**
 * Risk Level Enum
 */
export type RiskLevel = "acceptable" | "acceptable-clarify" | "not-acceptable";

/**
 * User Role Enum
 */
export type UserRole = "senior-counsel" | "managing-partner" | "associate" | "client";

/**
 * Review Row Type - The 7-column table structure
 */
export interface ReviewRow {
  section: string;
  pageNumber: number;
  sectionLink: string;
  modifiedBy?: string;
  modifiedWhen?: string;
  changeRationale?: string;
  riskLevel: RiskLevel;
  riskReasoning: string;
  suggestedAction: string;
  actionRationale: string;
  precedent?: string;
  suggestedWording: string;
  suggestedComment: string;
}

/**
 * Review Report Type
 */
export interface ReviewReport {
  summary: string;
  reviewTable: ReviewRow[];
  overallRiskScore: number;
  recommendations: string[];
}

/**
 * Custom Role Type
 */
export interface CustomRole {
  name: string;
  instructions: string;
}

/**
 * View State Enum
 */
export type ViewState = 'empty' | 'progress' | 'results' | 'error';

/**
 * Progress State
 */
export interface ProgressState {
  message: string;
  step?: string;
  progress: number;
  isConnected: boolean;
  logs: LogEntry[];
}

/**
 * Log Entry
 */
export interface LogEntry {
  timestamp: Date;
  message: string;
  step?: string;
}

/**
 * Form Data Type
 */
export interface ReviewFormData {
  backgroundInfo: string;
  agentInstructions: string;
  contractType: string;
  jurisdiction: string;
  userRole: string;
  roleInstructions: string;
  skipResearch: boolean;
  supportingDocs?: SupportingDocInput[];
}

export type SupportingDocType = "reference-contract" | "background";

export interface SupportingDocInput {
  id: string;
  file: File;
  docType: SupportingDocType;
}

export interface SupportingDocumentRecord {
  id: string;
  review_id: string;
  doc_type: SupportingDocType;
  file_path: string;
  file_name: string;
  extracted_text: string;
  created_at: string;
}

export const MAX_SUPPORTING_DOCS = 5;
export const MAX_SUPPORTING_DOC_BYTES = 10 * 1024 * 1024;

/**
 * SSE Event Types
 */
export type SSEEventType = 'run-started' | 'progress' | 'heartbeat' | 'complete' | 'error' | 'message';

/**
 * SSE Event Data
 */
export interface SSEEventData {
  runId?: string;
  message?: string;
  step?: string;
  progress?: number;
  timestamp?: number;
  status?: string;
  result?: ReviewReport;
  /** Set when the server persisted the review after workflow completion */
  reviewId?: string;
  /** Server-parsed contract text (e.g. from PDF); use for chat context */
  contractText?: string;
  error?: string;
  details?: unknown;
}

/**
 * App State
 */
export interface AppState {
  viewState: ViewState;
  selectedFile: File | null;
  customJurisdictions: string[];
  customContractTypes: string[];
  customRoles: Record<string, CustomRole>;
  customRoleInstructions: Record<string, string>;
  progress: ProgressState;
  results: ReviewReport | null;
  error: string | null;
  contractText: string | null;
  chat: ChatState;
  isLeftPanelOpen: boolean;
}

/**
 * Chat Message Part (AI SDK v5 format)
 */
export interface ChatMessagePart {
  type: 'text';
  text: string;
}

/**
 * Chat Message (AI SDK v5 format)
 */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  parts: ChatMessagePart[];
  /** ISO 8601 timestamp when the message was created */
  createdAt?: string;
  /** Parsed from embedded <<<review-sections ... >>> block in persisted content */
  attachedSections?: ReviewRow[];
}

/**
 * Chat State
 */
export interface ChatState {
  isOpen: boolean;
  threadId: string | null;
  messages: ChatMessage[];
  isLoading: boolean;
}

/**
 * Chat Message Record from database
 */
export interface ChatMessageRecord {
  id: string;
  review_id: string;
  user_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

/** Reference PDF row for a user-owned contract type (see contract_type_guideline_documents) */
export interface ContractTypeGuidelineDocumentRecord {
  id: string;
  contract_type_id: string;
  file_path: string;
  file_name: string;
  extracted_text: string;
  created_at: string;
}

/** Reference PDF row for a user-owned custom role (see custom_role_instruction_documents) */
export interface CustomRoleInstructionDocumentRecord {
  id: string;
  custom_role_id: string;
  file_path: string;
  file_name: string;
  extracted_text: string;
  created_at: string;
}

export interface ContractTypeRecord {
  id: string;
  name: string;
  value: string;
  guidelines: string | null;
  is_system: boolean;
  user_id: string | null;
  created_at: string;
  /** Populated in the client list for user-owned rows */
  guideline_document_count?: number;
}

export interface CustomRoleRecord {
  id: string;
  name: string;
  role_key: string;
  instructions: string;
  is_system: boolean;
  user_id: string | null;
  created_at: string;
  instruction_document_count?: number;
}

export interface ReviewVersionSummary {
  id: string;
  version: string;
  file_name: string | null;
  created_at: string;
}

export interface ReviewRecord {
  id: string;
  user_id: string;
  file_name: string | null;
  contract_file_path: string | null;
  contract_type: string | null;
  jurisdiction: string | null;
  user_role: string | null;
  background_info: string | null;
  agent_instructions: string | null;
  skip_research: boolean;
  summary: string | null;
  overall_risk_score: number | null;
  review_table: ReviewRow[] | null;
  recommendations: string[] | null;
  contract_text: string | null;
  status: string;
  root_review_id?: string | null;
  previous_review_id?: string | null;
  version_number?: number;
  version?: string;
  version_count?: number;
  versions?: ReviewVersionSummary[];
  created_at: string;
  updated_at?: string;
}

export type CollaboratorStatus = "pending" | "accepted" | "revoked";

export interface ReviewCollaborator {
  id: string;
  review_id: string;
  user_id: string | null;
  email: string;
  invited_by: string;
  invitation_token: string;
  status: CollaboratorStatus;
  invited_at: string;
  accepted_at: string | null;
  profiles?: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

export interface InvitationDetails {
  reviewId: string;
  email: string;
  status: CollaboratorStatus;
  reviewFileName: string | null;
  reviewContractType: string | null;
  inviterName: string;
  invitedAt: string;
}

export interface ReviewWithOwner extends ReviewRecord {
  owner_name?: string | null;
  is_owner: boolean;
}

export interface ReviewShareUser {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  email: string;
}

export interface Discussion {
  id: string;
  review_id: string;
  created_by: string;
  title: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  message_count?: number;
  last_message_at?: string | null;
  is_unread?: boolean;
}

export interface DiscussionMessage {
  id: string;
  discussion_id: string;
  user_id: string;
  content: string;
  created_at: string;
  profiles?: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  mentions?: DiscussionMention[];
}

export interface DiscussionMention {
  id: string;
  message_id: string;
  user_id: string;
  notified_at: string | null;
  profiles?: {
    full_name: string | null;
  } | null;
}

export interface DiscussionReadStatus {
  id: string;
  discussion_id: string;
  user_id: string;
  last_read_at: string;
}

export interface CreateDiscussionPayload {
  title: string;
  content: string;
}

export interface CreateMessagePayload {
  content: string;
  mentions?: string[];
}

export interface CollaboratorUserInfo {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  email: string;
}
